import * as React from 'react'

import { Card } from '@/app/features/common/Card'
import { FooterWithButtons } from '../../components/FooterWithButtons'
import { DriversProvider } from '@/modules/shared/context/drivers/DriversContext'
import { TaskProvider } from '@/modules/shared/context/TaskContext'
import { GroupPayrollView } from '@/modules/drivers/tabs/registry/views/payroll/GroupPayrollView'
import { useDateStore } from '@/modules/drivers/stores/dateStore'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'
import { SelectedDriver } from '@/app/types/creator'

type Props = {
  companyId: string
  selectedDrivers: SelectedDriver[]
  month: string // 'YYYY-MM'
  refreshEvidenceRows?: () => Promise<void>
  onNext: () => void
  onPrev: () => void
}

export const EvidenceDetailsSubstep = ({
  companyId,
  selectedDrivers,
  month,
  refreshEvidenceRows,
  onNext,
  onPrev,
}: Props) => {
  const selectedDriverIds = React.useMemo(
    () =>
      selectedDrivers
        .map((d) => Number(d.driverId))
        .filter((id) => Number.isFinite(id)),
    [selectedDrivers],
  )

  React.useEffect(() => {
    const dateStore = useDateStore.getState()
    const sideStore = useSideSelectionStore.getState()

    const prevMonth = dateStore.month
    const prevSideIds = sideStore.sideCheckedIds
    const prevSideActive = sideStore.sideIsActive

    dateStore.setMonth(month)
    sideStore.toggleAllSide(selectedDriverIds, true, true)

    return () => {
      useDateStore.getState().setMonth(prevMonth)
      useSideSelectionStore.setState({
        sideCheckedIds: prevSideIds,
        sideIsActive: prevSideActive,
      })
    }
  }, [companyId, month, selectedDriverIds])

  const submitRef = React.useRef<(() => void) | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const handleNext = () => {
    if (isSubmitting) return
    if (!submitRef.current) {
      onNext()
      return
    }
    setIsSubmitting(true)
    submitRef.current()
  }

  const handleSubmitSuccess = React.useCallback(async () => {
    try {
      if (refreshEvidenceRows) {
        await refreshEvidenceRows()
      }
    } catch (err) {
      console.error('Refreshing evidence rows after submit failed', err)
    } finally {
      setIsSubmitting(false)
      onNext()
    }
  }, [refreshEvidenceRows, onNext])

  const handleSubmitError = React.useCallback(() => {
    setIsSubmitting(false)
  }, [])

  return (
    <Card
      className="tw-flex tw-min-h-0 tw-max-w-full tw-flex-1 tw-flex-col tw-bg-base-100 tw-pb-3"
      classNameContent="!tw-flex !tw-px-0 !tw-pt-0 tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"
      paddingTop={false}
      noContentPadding
    >
      <TaskProvider>
        <DriversProvider>
          <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
            <GroupPayrollView
              companyId={companyId}
              hideActionBar
              hideExtraColumns
              submitRef={submitRef}
              onSubmitSuccess={handleSubmitSuccess}
              onSubmitError={handleSubmitError}
            />
          </div>
        </DriversProvider>
      </TaskProvider>

      <FooterWithButtons
        onNext={handleNext}
        onPrev={onPrev}
        className="tw-shrink-0 tw-border-t tw-border-base-300"
        nextText={isSubmitting ? 'Przeliczanie...' : 'Następny krok'}
        isNextDisabled={isSubmitting}
      />
    </Card>
  )
}
